import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { selectResultDetail, sendMessage } from '@patient-ui/patient-web/store';
import {
  OrbiSuggestedPrompt,
  STATIC_RESULT_PROMPTS,
  STATIC_RESULT_PROMPT_HEADING,
  STATIC_RESULT_PROMPT_SUBTEXT,
} from '@patient-ui/shared/constants';
import { buildOrbiRequestContext } from '@patient-ui/shared/utils';

import { LayoutService } from '../../../shared/services/layout.service';
import { OrbiTrackingService } from '../../services/orbi-tracking.service';

/** Which way the popover's caret/tail points at the Orbi entry bar. */
export type StaticPromptCaretDirection = 'up' | 'down';

/**
 * "Make Sense of Your Results" static-prompt popover BODY (static-prompts MVP).
 *
 * The violet card that offers a fixed set of starter questions on the Result
 * Details screen. Per the approved Figma it is NOT a banner in the results
 * content flow: it is a floating POPOVER anchored to the Orbi "Ask a Question"
 * entry bar (`patient-ui-orbi-entry-button`), with a caret/tail that points at
 * that bar. This component renders only the popover's body + caret; the entry
 * bar owns WHERE it appears, WHEN it auto-shows, and WHICH WAY the caret points
 * (see `OrbiEntryButtonComponent`).
 *
 * Selecting a prompt submits it to the existing Orbi agent through the SAME
 * launch path the entry bar uses (`OrbiEntryButtonComponent.onAsk`): it opens
 * the panel and dispatches `sendMessage` with the live request context, and
 * records the existing non-PHI `agent_message_sent` Pendo event via
 * `OrbiTrackingService.trackMessageSent` with `input_method: 'suggested_prompt'`.
 * The feature neither modifies the agent nor introduces a new launch mechanism
 * or analytics event. A system-generated prompt is treated identically to a
 * user-typed one — the same action, the same request context, no privileged
 * branch.
 *
 * Eligibility seam: the entry bar decides when to mount/show this popover. It
 * auto-shows on the Result Details route (the later enhanced-result-only
 * restriction plugs into the host's `shouldShow` seam, not here, so this stays a
 * rewrite-free change). Dismiss is view-local — it emits `dismiss` and the host
 * hides the popover (no persistence, no analytics, no PHI); the host's bulb
 * affordance re-opens it.
 */
@Component({
  selector: 'patient-ui-static-prompt-card',
  templateUrl: './static-prompt-card.component.html',
  styleUrls: ['./static-prompt-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaticPromptCardComponent {
  /**
   * Which way the popover's caret points at the entry bar. `up` (default) drops
   * the popover BELOW the bar with the caret pointing up at it — the web layout,
   * where the bar is top-right. `down` floats the popover ABOVE the bar with the
   * caret pointing down at it — the mobile/docked layout, where the bar is
   * bottom-docked. The host binds this from the active breakpoint.
   */
  @Input() caretDirection: StaticPromptCaretDirection = 'up';

  /** Emitted when the patient dismisses the popover (X). View-local, no PHI. */
  @Output() dismiss = new EventEmitter<void>();

  readonly heading = STATIC_RESULT_PROMPT_HEADING;
  readonly subtext = STATIC_RESULT_PROMPT_SUBTEXT;

  /**
   * The default prompt set. Rendered 1..N from this one source (FR1.3); the MVP
   * ships two. Not personalized, no PHI.
   */
  readonly prompts: readonly OrbiSuggestedPrompt[] = STATIC_RESULT_PROMPTS;

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly layoutService: LayoutService,
    private readonly tracking: OrbiTrackingService
  ) {}

  /**
   * User selected a prompt: record the non-PHI analytics event, open the Orbi
   * panel, and dispatch the message so the answer is already loading when the
   * panel appears — the same open-and-dispatch the entry bar performs. The
   * effect (PCSA-13164) owns the session id, Split gate, transport, and the
   * error experience, so this card never branches on launch success/failure.
   */
  onPromptClick(prompt: OrbiSuggestedPrompt): void {
    this.tracking.trackMessageSent('suggested_prompt');
    this.layoutService.setOrbiHistoryOpen(false);
    this.layoutService.setOrbiPanelOpen(true);
    this.store
      .select(selectResultDetail)
      .pipe(take(1))
      .subscribe((resultDetail) =>
        this.store.dispatch(
          sendMessage({
            userMessage: prompt.fullPrompt,
            requestContext: buildOrbiRequestContext(this.router.url, {
              resultDetail,
            }),
          })
        )
      );
  }

  /** Dismiss the popover (no persistence, no analytics, no PHI). */
  onDismiss(): void {
    this.dismiss.emit();
  }

  trackByPrompt(_index: number, prompt: OrbiSuggestedPrompt): string {
    return prompt.shortPrompt;
  }
}
